import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveTab,
  updateConsolidatedInvoice,
} from "src/redux/slices/invoiceSlice";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import {
  generateConsolidatedInvoiceURLHandler,
  isConsolidatedInvoiceNotPaid,
  isConsolidatedInvoicePaid,
  isConsolidatedInvoicePGW,
} from "src/utils/common";
import {
  convertDateIFNotMin,
  getTextValueById,
  isNullOrWhiteSpace,
} from "src/utils/helpers";
import { confirmDialog } from "primereact/confirmdialog";
import { RoutePaths } from "src/resources/enum";
import { useNavigate } from "react-router";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import { ReactComponent as DeleteIcon } from "src/assets/icons/delete.svg";

const ConsolidatedInvoiceDefaultView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "invoice", "registration"]);
  const { selectedItems } = useSelector((state: any) => state.invoices);
  const {
    consolidatedInvoiceId,
    totalAmount,
    status,
    consolidatedInvoiceNumber,
    paymentDate,
    invoiceList,
    totalAmountWithoutTax,
    totalTax,
  } = useSelector(
    (state: any) => state.invoices.consolidatedInvoiceDetailsPopupData
  );
  const { consolidatedInvoiceStatusOptions } =
    useSelector((state: any) => state.lookup);
  const accept = (invoiceId: string, isLastInvoice = false) => {
    dispatch(
      updateConsolidatedInvoice(true, consolidatedInvoiceId, [invoiceId]) as any
    ).then(() => {
      dispatch(setActiveTab(1));
      if (isLastInvoice) {
        navigate(RoutePaths.ViewInvoices);
      }
    });
  };

  const reject = () => {};
  const confirm = (invoiceId: any) => {
    confirmDialog({
      message:
        selectedItems.length === 1
          ? t("deleteLastInvoice")
          : t("deleteInvoice"),
      header: t("deleteConfirmation"),
      icon: "pi pi-info-circle",
      acceptLabel: t("yes"),
      rejectLabel: t("no"),
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => accept(invoiceId, selectedItems.length === 1),
      reject,
    });
  };
  function removeInvoiceHandler(invoiceId: string): void {
    confirm(invoiceId);
  }
  const isNotPaid = isConsolidatedInvoiceNotPaid(status);
  const isPaid = isConsolidatedInvoicePaid(status);
  const isPending = isConsolidatedInvoicePGW(status);
  const showPayButton = selectedItems?.length > 0 && isNotPaid;

  let paymentStatusClass = "in-progress";
  if (isNotPaid) {
    paymentStatusClass = "not-paid";
  } else if (isPaid) {
    paymentStatusClass = "completed";
  }
  const invoiceTotals = [
    {
      label: t("invoiceDetails.totalAmountWithoutTax"),
      value: totalAmountWithoutTax,
    },
    {
      label: t("invoiceDetails.totalTax"),
      value: totalTax,
    },
    {
      label: t("totalAmount"),
      value: totalAmount,
    },
  ];
  return (
    <div className="invoice-details flex-column default-view">
      <section className="invoice-header mb-0">
        <div className="invoice-header__details">
          <div className="invoice-header__details__info d-flex flex-column">
            <div className="invoice-header__details__info__row d-flex justify-content-between">
              <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between pe-2 py-2">
                <h6>{t("invoiceDetails.consolidatedInvoiceNumber")}</h6>
                <span>{consolidatedInvoiceNumber}</span>
              </div>
              <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between pe-2 py-2">
                {!showPayButton && !isPending && (
                  <h6>{t("invoiceDetails.paymentDate")}</h6>
                )}
                <span> {convertDateIFNotMin(paymentDate)}</span>
              </div>
              <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-between pe-2 py-2">
                <h6>{t("invoiceStatus")}</h6>
                <span className={`fit-content oval ${paymentStatusClass}`}>
                  {Number(status)
                    ? getTextValueById(
                        status,
                        consolidatedInvoiceStatusOptions,
                        "id"
                      )
                    : status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="invoice-body invoice-amount mt-1">
        {/* <div className="invoice-body__title py-2">
          {t("invoiceDetails.hassantukInvoiceDetails")}
        </div> */}
        <div className="invoice-body__sub-title py-1 ps-3 pe-2 d-flex justify-content-between align-items-start">
          <span className="w-25 cell-header">
            {t("invoiceDetails.invoiceNumber")}
          </span>
          <span className="w-25 cell-header">{t("contractNo")}</span>
          <span className="w-25 cell-header">{t("assignedBuilding")}</span>
          {/* <span className="w-20 cell-header">{t("paymentDueDate")}</span> */}
          <span className="w-15 cell-header">
            {t("invoiceDetails.amountAED")}
          </span>
          {showPayButton && (
            <span className="w-5 cell-header action-header">{t("action")}</span>
          )}
        </div>
        <div className="invoice-header__details__info d-flex flex-column">
          {invoiceList?.map((invoice: any) => (
            <div key={invoice.invoiceNumber}>
              <div className="invoice-header__details__info__row d-flex justify-content-between px-2">
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-between w-25 pt-2">
                  <span>{invoice.invoiceNumber}</span>
                </div>
                <VerticalDivider />
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-25 pt-2">
                  <span>{invoice.contractNumber}</span>
                </div>
                <VerticalDivider />
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-25 pt-2">
                  <span>{invoice.buildingName}</span>
                </div>
                <VerticalDivider />
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-25 pt-2">
                  <span>{invoice.totalAmount}</span>
                </div>
                {showPayButton && (
                  <>
                    <VerticalDivider />
                    <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-5 pt-2">
                      <DeleteIcon
                        className="cursor-pointer delete-icon"
                        onClick={() => removeInvoiceHandler(invoice.invoiceId)}
                      />
                    </div>
                  </>
                )}
              </div>
              <HorizontalDivider />
            </div>
          ))}

          {invoiceTotals.map((item, index) => (
            <React.Fragment key={index}>
              <div
                className={`invoice-header__details__info__row total d-flex px-2 w-100  ${
                  index === invoiceTotals.length - 1 ? "total-with-tax" : ""
                }`}
              >
                <div
                  className={`invoice-header__details__info__row__item d-flex align-items-center justify-content-end total-label-container  ${
                    showPayButton ? "with-action" : "without-action"
                  }`}
                >
                  <h6 className="w-30 text-start px-2">{item.label}</h6>
                </div>
                <VerticalDivider />
                <div
                  className={`invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-25`}
                >
                  <span>{item.value}</span>
                </div>
              </div>
              <HorizontalDivider />
            </React.Fragment>
          ))}
        </div>
        <HorizontalDivider />
        {isPending && (
          <h4 className="locked-invoice text-center mt-2">
            {t("lockedInvoice")}
          </h4>
        )}
      </section>
    </div>
  );
};

export default ConsolidatedInvoiceDefaultView;
